import type { AgentRunRow } from '../../lib/database.types'
import { toTickets, type Ticket } from './tickets'

/**
 * How work in one department performed, per ticket.
 *
 * The grouping is the one `tickets.ts` already does -- runs collected by the
 * reference they carry -- and this adds the figures a desk asks for on top of
 * it rather than regrouping the rows a second time. What it adds is the shape
 * of the loop: how many times a ticket was built, how many of those were trips
 * it should not have needed, and where the money and the time went.
 *
 * Three things this file is careful about, all of them ways of reporting a
 * number that is not true:
 *
 *   a build is a branch   `branch_name` is null until `create_branch` succeeds,
 *                         so a run cancelled at claim time -- which is what the
 *                         staleness check does -- did no work. Counting raw
 *                         rows would make the guard that prevents churn look
 *                         like the cause of it. DEV-12 has three build rows and
 *                         was built once.
 *   two clocks            agent time and wall clock measure different things
 *                         and are never added or averaged together. DEV-12 took
 *                         78 minutes wall clock against single-digit minutes of
 *                         agent time, because the rest was waiting for a
 *                         person. A page that blends them reports neither.
 *   cost is cost_usd      no token column is accumulated into any total here.
 *                         They are priced differently and the sum would not be
 *                         the basis of any charge.
 *
 * Two bounds it cannot see from `agent_runs`, both stated on the page rather
 * than papered over. A redraft that superseded a hand-written specification is
 * the ticket's only spec run, so it reads as zero respecs -- the truthful
 * signal is the `spec.superseded` event, which is a different table. And work
 * discarded inside a retried run is invisible: DEV-11's build shows `completed`
 * with a single cost, and inside it a finished model call was thrown away when
 * the push collided. Row-level cost cannot see it; only `events` can.
 */

/**
 * Which part of the loop a run belongs to.
 *
 * Read from the lane type's segments -- 'dev-build' is a build because one of
 * its segments is 'build', not because it starts with 'dev'. A department is
 * never named here (product rule 2), so 'finance-review' is a review the day
 * finance comes online, with no code change. A lane naming none of the three
 * is `other`: it still carries its cost into the total rather than being
 * dropped, because money that was spent was spent.
 */
export type Phase = 'spec' | 'build' | 'review' | 'other'

const NAMED = new Set<string>(['spec', 'build', 'review'])

export function phaseOf(laneType: string): Phase {
  for (const part of laneType.toLowerCase().split(/[^a-z0-9]+/)) {
    if (NAMED.has(part)) return part as Phase
  }
  return 'other'
}

/** Spend per part of the loop. The total lives on `Ticket.spend`, not here. */
export interface PhaseCost {
  spec: number
  build: number
  review: number
  other: number
}

export interface DeskTicket extends Ticket {
  cost: PhaseCost
  /** Build runs that reached a branch. A cancelled claim is not a build. */
  builds: number
  /** Builds beyond the first: trips around the loop that produced nothing new. */
  retrips: number
  /** Completed spec runs beyond the first. Undercounts a hand-written original. */
  respecs: number
  /** Summed run time, in whole seconds. Null when no run recorded both ends. */
  agentSeconds: number | null
  /**
   * Last `ended_at` minus first `created_at`, in whole seconds. Null while
   * nothing has ended -- a ticket still in flight has no wall clock yet, and
   * measuring it to now would make an idle queue look like work.
   */
  wallClockSeconds: number | null
}

const time = (iso: string | null): number | null => {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isNaN(t) ? null : t
}

/** Milliseconds between two timestamps, or null if either is missing. */
function between(startIso: string | null, endIso: string | null): number | null {
  const start = time(startIso)
  const end = time(endIso)
  if (start === null || end === null) return null
  return Math.max(0, end - start)
}

function decorate(ticket: Ticket, rows: ReadonlyArray<AgentRunRow>): DeskTicket {
  const cost: PhaseCost = { spec: 0, build: 0, review: 0, other: 0 }
  let builds = 0
  let completedSpecs = 0
  let agentMs = 0
  let timedRuns = 0
  let firstCreated: number | null = null
  let lastEnded: number | null = null

  for (const run of rows) {
    const phase = phaseOf(run.lane_type)

    if (run.cost_usd != null) {
      const money = Number(run.cost_usd)
      if (Number.isFinite(money)) cost[phase] += money
    }

    // The branch is the evidence. A build row with none never got past claiming
    // the ticket, so it is neither a build nor a retrip.
    if (phase === 'build' && run.branch_name) builds += 1
    // A spec run that failed produced no specification, so it is not a respec
    // either -- it is the same specification, attempted twice.
    if (phase === 'spec' && run.status === 'completed') completedSpecs += 1

    const ran = between(run.started_at, run.ended_at)
    if (ran !== null) {
      agentMs += ran
      timedRuns += 1
    }

    const created = time(run.created_at)
    if (created !== null && (firstCreated === null || created < firstCreated)) firstCreated = created
    const ended = time(run.ended_at)
    if (ended !== null && (lastEnded === null || ended > lastEnded)) lastEnded = ended
  }

  return {
    ...ticket,
    cost,
    builds,
    retrips: Math.max(0, builds - 1),
    respecs: Math.max(0, completedSpecs - 1),
    agentSeconds: timedRuns > 0 ? Math.round(agentMs / 1000) : null,
    wallClockSeconds:
      lastEnded !== null && firstCreated !== null ? Math.round(Math.max(0, lastEnded - firstCreated) / 1000) : null,
  }
}

/**
 * One department's tickets, newest activity first -- the order `toTickets`
 * puts them in, which is not a ranking and deliberately does not lift the ones
 * having a hard time to the top.
 *
 * Reads the rows `useRuns()` already fetched; there is no query here. That
 * makes it a window rather than a history -- the department's own window,
 * `useRuns(slug)`, the same one the lanes view reads under a filter -- and
 * the page says so.
 */
export function toDeskTickets(runs: ReadonlyArray<AgentRunRow>, department: string): DeskTicket[] {
  const mine = runs.filter((run) => run.department === department)

  // Grouped a second time only to hand each ticket its own rows back; the
  // grouping that matters -- and every field derived from it -- is toTickets'.
  // A run with no external_ref belongs to no ticket, which is the rule there
  // too, so the two sets of refs agree.
  const byRef = new Map<string, AgentRunRow[]>()
  for (const run of mine) {
    const ref = run.external_ref
    if (!ref) continue
    const held = byRef.get(ref)
    if (held) held.push(run)
    else byRef.set(ref, [run])
  }

  return toTickets(mine).map((ticket) => decorate(ticket, byRef.get(ticket.ref) ?? []))
}

export interface DeskSummary {
  tickets: number
  /** Sum of cost_usd across the department's tickets. No token column is in it. */
  spend: number
  /** False when no run recorded a cost, so the view says '—' and not '$0.00'. */
  hasCost: boolean
  cost: PhaseCost
  builds: number
  retrips: number
  respecs: number
  /**
   * Summed agent time. Additive, because two runs each took the time they took.
   *
   * There is deliberately no total wall clock beside it: tickets overlap in
   * time, so adding their spans would produce a figure longer than the night
   * they happened in. The median ticket is what the view shows instead.
   */
  agentSeconds: number | null
  medianAgentSeconds: number | null
  medianWallClockSeconds: number | null
  /** How many tickets each median is over, so the view can say so. */
  agentTimedTickets: number
  wallClockTickets: number
}

/** Median, not mean: one long-waiting ticket should not move the headline. */
function median(values: ReadonlyArray<number>): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid]! : Math.round((sorted[mid - 1]! + sorted[mid]!) / 2)
}

export function deskSummary(tickets: ReadonlyArray<DeskTicket>): DeskSummary {
  const cost: PhaseCost = { spec: 0, build: 0, review: 0, other: 0 }
  let spend = 0
  let hasCost = false
  let builds = 0
  let retrips = 0
  let respecs = 0
  let agentSeconds = 0
  const agentTimes: number[] = []
  const wallClocks: number[] = []

  for (const ticket of tickets) {
    cost.spec += ticket.cost.spec
    cost.build += ticket.cost.build
    cost.review += ticket.cost.review
    cost.other += ticket.cost.other
    spend += ticket.spend
    if (ticket.hasCost) hasCost = true
    builds += ticket.builds
    retrips += ticket.retrips
    respecs += ticket.respecs
    if (ticket.agentSeconds !== null) {
      agentSeconds += ticket.agentSeconds
      agentTimes.push(ticket.agentSeconds)
    }
    if (ticket.wallClockSeconds !== null) wallClocks.push(ticket.wallClockSeconds)
  }

  return {
    tickets: tickets.length,
    spend,
    hasCost,
    cost,
    builds,
    retrips,
    respecs,
    agentSeconds: agentTimes.length > 0 ? agentSeconds : null,
    medianAgentSeconds: median(agentTimes),
    medianWallClockSeconds: median(wallClocks),
    agentTimedTickets: agentTimes.length,
    wallClockTickets: wallClocks.length,
  }
}
